import React, { useState } from 'react';
import AUTH_CONFIG from '../config/auth';
import DemoCredentials from './DemoCredentials';

const LoginPage = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Validate credentials using AUTH_CONFIG
      if (!formData.email || !formData.password) {
        throw new Error('Please enter both email and password');
      }

      const user = AUTH_CONFIG.validateCredentials(formData.email, formData.password);
      
      if (!user) {
        throw new Error('Invalid email or password. Please check your credentials and try again.');
      }

      // Generate token
      const token = AUTH_CONFIG.generateToken(user);
      
      // Store in localStorage if remember me is checked, otherwise sessionStorage
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('userToken', token);
      storage.setItem('userData', JSON.stringify(user));
      
      onLogin(user);
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a1a1a 0%, #000000 100%)',
      position: 'relative',
      overflow: 'hidden'
    },
    overlay: {
      position: 'absolute',
      inset: 0,
      background: 'radial-gradient(circle at 20% 80%, rgba(66, 133, 244, 0.1) 0%, transparent 50%)',
      pointerEvents: 'none'
    },
    formWrapper: {
      position: 'relative',
      zIndex: 10,
      width: '100%',
      maxWidth: '400px',
      padding: '0 16px'
    },
    formCard: {
      backgroundColor: 'rgba(36, 36, 36, 0.95)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      border: '1px solid #333'
    },
    logoContainer: {
      textAlign: 'center',
      marginBottom: '32px'
    },
    logo: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #4285f4 0%, #1976d2 100%)',
      color: 'white',
      marginBottom: '16px',
      boxShadow: '0 10px 15px -3px rgba(66, 133, 244, 0.3)'
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#ffffff',
      margin: '0 0 8px 0'
    },
    subtitle: {
      color: '#aaa',
      fontSize: '14px',
      margin: 0
    },
    errorAlert: {
      marginBottom: '24px',
      padding: '16px',
      backgroundColor: 'rgba(244, 67, 54, 0.1)',
      border: '1px solid rgba(244, 67, 54, 0.3)',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    errorText: {
      color: '#ff6b6b',
      fontSize: '14px',
      margin: 0
    },
    inputGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#e0e0e0',
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #444',
      borderRadius: '8px',
      backgroundColor: '#2a2a2a',
      color: '#ffffff',
      fontSize: '16px',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxSizing: 'border-box'
    },
    passwordContainer: {
      position: 'relative'
    },
    passwordToggle: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      color: '#aaa',
      cursor: 'pointer',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    rememberRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '24px'
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      color: '#e0e0e0',
      cursor: 'pointer'
    },
    checkbox: {
      marginRight: '8px'
    },
    forgotLink: {
      fontSize: '14px',
      color: '#4285f4',
      textDecoration: 'none',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0
    },
    submitButton: {
      width: '100%',
      padding: '12px 16px',
      background: 'linear-gradient(135deg, #4285f4 0%, #1976d2 100%)',
      color: 'white',
      fontWeight: '500',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '16px',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    divider: {
      margin: '24px 0',
      position: 'relative',
      textAlign: 'center'
    },
    dividerLine: {
      position: 'absolute',
      top: '50%',
      left: 0,
      right: 0,
      height: '1px',
      backgroundColor: '#444'
    },
    dividerText: {
      position: 'relative',
      display: 'inline-block',
      padding: '0 16px',
      backgroundColor: '#242424',
      color: '#888',
      fontSize: '14px'
    },
    ssoButton: {
      width: '100%',
      padding: '12px 16px',
      backgroundColor: 'transparent',
      color: '#e0e0e0',
      fontWeight: '500',
      borderRadius: '8px',
      border: '1px solid #444',
      cursor: 'pointer',
      fontSize: '16px',
      transition: 'all 0.2s'
    },
    footer: {
      textAlign: 'center',
      marginTop: '24px'
    },
    footerText: {
      fontSize: '14px',
      color: '#aaa',
      margin: 0
    },
    footerLink: {
      color: '#4285f4',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0,
      fontWeight: '500'
    },
    copyright: {
      textAlign: 'center',
      marginTop: '24px',
      color: '#666',
      fontSize: '12px'
    },
    spinner: {
      width: '20px',
      height: '20px',
      border: '2px solid #ffffff',
      borderTopColor: 'transparent',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.overlay}></div>
      
      <div style={styles.formWrapper}>
        <div style={styles.formCard}>
          <div style={styles.logoContainer}>
            <div style={styles.logo}>
              <svg width="40" height="40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 11h.01M12 9v2"></path>
              </svg>
            </div>
            <h1 style={styles.title}>POCUS CLOUD</h1>
            <p style={styles.subtitle}>Sign in to access your ultrasound studies</p>
          </div>

          {error && (
            <div style={styles.errorAlert}>
              <span style={styles.errorText}>{error}</span>
              <button
                onClick={() => setError('')}
                style={{ ...styles.passwordToggle, position: 'static', transform: 'none' }}
              >
                ×
              </button>
            </div>
          )}

          <div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                style={styles.input}
                placeholder="you@example.com"
                required
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.passwordContainer}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onKeyPress={handleKeyPress}
                  style={{ ...styles.input, paddingRight: '48px' }}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.passwordToggle}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div style={styles.rememberRow}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={styles.checkbox}
                />
                Remember me
              </label>
              <button
                type="button"
                onClick={() => alert('Password reset functionality would go here')}
                style={styles.forgotLink}
              >
                Forgot password?
              </button>
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                ...styles.submitButton,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? (
                <div style={styles.spinner}></div>
              ) : (
                <>
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Sign In
                </>
              )}
            </button>

            <div style={styles.divider}>
              <div style={styles.dividerLine}></div>
              <span style={styles.dividerText}>OR</span>
            </div>

            <button
              type="button"
              onClick={() => alert('SSO login would be implemented here')}
              style={styles.ssoButton}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#333';
                e.target.style.borderColor = '#555';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = '#444';
              }}
            >
              Sign in with Hospital SSO
            </button>
          </div>

          <div style={styles.footer}>
            <p style={styles.footerText}>
              Don't have an account?{' '}
              <button
                onClick={() => alert('Registration functionality would go here')}
                style={styles.footerLink}
              >
                Contact Administrator
              </button>
            </p>
          </div>
        </div>

        <p style={styles.copyright}>
          © 2025 POCUS CLOUD. All rights reserved.
        </p>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        input:focus {
          border-color: #4285f4 !important;
        }
        
        button:hover {
          opacity: 0.9;
        }
      `}</style>
      
      <DemoCredentials />
    </div>
  );
};

export default LoginPage;
